import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';

import {
  cn,
  NavigationMenu,
  NavigationMenuList,
} from '@sandtech-hub-monorepo/ui-kit';

import { Settings } from './components/Settings';
import { AppsCenter } from './components/AppsCenter';
import { NotificationsCenter } from './components/NotificationsCenter';
import { useGetMaybeViewer } from '@sandtech-hub-monorepo/ui-auth-kit';
import { makeVar, useQuery, useReactiveVar } from '@apollo/client';
import { LegacyPoints } from './components/LegacyPoints';
import {
  AppInterface,
  SettingsAppInterface,
  usePlatforms,
  useIsSet,
} from '../../utils';
import { HeaderLink } from './components/HeaderLink';
import { AttendanceDialog } from '../SelfCheckIn/AttendanceDialog';
import { PaywallDialog } from './components/PaywallDialog';
import { DataWallDialog } from './components/DataWallDialog';
import {
  BillingState,
  GetProgramEnrollmentPaymentPlansDocument,
  ProgramEnrollmentStatus,
} from '@sandtech-hub-monorepo/ehub-client';
import { canPopupBeDisplayed, isPaywallOpen } from './utils';
import { hasCompletedDataCollection } from '../components/data-collection/DataCollectionModal';

export type HeaderProps = {
  homeRoute: string;
  sdk: boolean;
  hasThemeSwitcher: boolean;
  withLogo: boolean;
  apps: AppInterface[];
  settingsApps: SettingsAppInterface[];
  logo: JSX.Element;
  rewardsLink?: string;
  showNotifications?: boolean;
  className?: string;
};

export const checkInModalState = makeVar<{
  open: boolean;
}>({
  open: false,
});

export function Header({ showNotifications = true, ...props }: HeaderProps) {
  const [dataWallClosed, setDataWallClosed] = useState(false);
  const [navigationTab, setNavigationTab] = useState('');
  const { viewer } = useGetMaybeViewer();
  const { current } = usePlatforms(process.env['NX_COOKIE_DOMAIN'] || '');
  const isSet = useIsSet();

  const [pollForPaymentPlans, setPollForPaymentPlans] = useState(false);
  const { data: paymentPlansData } = useQuery(
    GetProgramEnrollmentPaymentPlansDocument,
    { pollInterval: pollForPaymentPlans ? 5000 : undefined, skip: !viewer }
  );

  const noPaymentPlan = useMemo(
    () => paymentPlansData?.viewer.paymentPlans.length === 0,
    [paymentPlansData]
  );

  const ongoingPathwayPaymentPlanId = useMemo(
    () =>
      paymentPlansData?.viewer.programEnrollments?.find(
        ({ status, program }) =>
          status === ProgramEnrollmentStatus.Ongoing &&
          ['ALX_PATHWAYS', 'BS.DS', 'BS.SE.BE', 'BS.SE.FE'].includes(
            program.abbreviation
          )
      )?.paymentPlan?.paymentPlanId,
    [paymentPlansData]
  );

  const paymentPlanAtRisk = useMemo(
    () =>
      paymentPlansData?.viewer.paymentPlans?.find(
        ({ billingState, paymentPlanId, code }) =>
          (!ongoingPathwayPaymentPlanId
            ? code === 'all-access'
            : paymentPlanId === ongoingPathwayPaymentPlanId) &&
          (billingState === BillingState.AtRisk ||
            billingState === BillingState.Inactive ||
            billingState === BillingState.Cancelled)
      ),
    [paymentPlansData, ongoingPathwayPaymentPlanId]
  );

  const hasEverPaid = useMemo(
    () =>
      paymentPlansData?.viewer.paymentPlans?.some(
        ({ totalPaidAmount }) => totalPaidAmount && totalPaidAmount > 0
      ) ?? false,
    [paymentPlansData]
  );

  useEffect(() => {
    setPollForPaymentPlans(!!paymentPlanAtRisk);
  }, [paymentPlanAtRisk]);

  const [paywallDismissed, setPaywallDismissed] = useState(false);

  const checkInModal = useReactiveVar(checkInModalState);

  useEffect(() => {
    setNavigationTab(''); // Url changes upon clicks don't trigger the hiding of the app drawer.
  }, [window?.location]);

  const paywallOpen =
    !paywallDismissed &&
    isPaywallOpen(
      noPaymentPlan,
      !!paymentPlanAtRisk,
      viewer?.skipPaywallUntil,
      current.id,
      true
    );

  return (
    <div
      className={cn(
        'relative flex gap-3 z-20 justify-between px-4 py-2 xxs:px-6 xxs:py-4 align-middle bg-background shadow-md dark:shadow-none min-h-[4.5rem]',
        props.sdk ? 'shadow-none' : 'shadow-md',
        props.className
      )}
    >
      {props.withLogo ? (
        <HeaderLink
          sdk={props.sdk}
          href={props.homeRoute}
          className="flex items-center py-1.5 hover:text-primary transition-colors duration-200"
        >
          {props.logo}
        </HeaderLink>
      ) : (
        <div></div>
      )}
      <NavigationMenu value={navigationTab} onValueChange={setNavigationTab}>
        <NavigationMenuList className="flex gap-2">
          {!viewer ? null : (
            <>
              {props.rewardsLink && (
                <LegacyPoints
                  className="flex cursor-pointer"
                  rewardsLink={props.rewardsLink}
                />
              )}
              {showNotifications && (
                <NotificationsCenter className="flex" sdk={props.sdk} />
              )}
              {props.apps.length > 0 && (
                <AppsCenter
                  className="flex"
                  sdk={props.sdk}
                  apps={props.apps}
                />
              )}
              <Settings
                className="flex"
                hasThemeSwitcher={props.hasThemeSwitcher}
                sdk={props.sdk}
                apps={props.settingsApps}
              />
            </>
          )}
        </NavigationMenuList>
      </NavigationMenu>
      {!viewer ? null : (
        <AttendanceDialog
          open={checkInModal.open}
          handleOpenChange={() => checkInModalState({ open: false })}
        />
      )}

      <PaywallDialog
        isPathway={!!ongoingPathwayPaymentPlanId}
        isSet={isSet}
        open={paywallOpen}
        onClose={() => setPaywallDismissed(true)}
        paymentPlan={paymentPlanAtRisk}
        sdk={props.sdk}
        className={isSet ? 'bg-white' : ''}
      />
      {!paywallOpen && (
        <DataWallDialog
          open={
            !isSet &&
            !!viewer &&
            hasEverPaid &&
            canPopupBeDisplayed(current.id) &&
            !hasCompletedDataCollection(viewer) &&
            !dataWallClosed
          }
          viewer={viewer}
          onComplete={() => setDataWallClosed(true)}
        />
      )}
    </div>
  );
}
